import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";

import Header from "../elements/Header";
import Loading from "../elements/Loading";
import InlineNotification from "../elements/InlineNotification";
import OneSelectUI from "../elements/inputs/one-select";
import SubmitButton from "../elements/inputs/SubmitButton";
import getAPIClient from "../hooks/apiClient";
import s from "../styles/pages/resume_batch.module.scss";

function parseIds(input) {
    if (!input) return [];
    return input
        .split(',')
        .map((value) => Number(value.trim()))
        .filter((value) => Number.isFinite(value));
}

function safeNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function getStatusMeta(status) {
    if (status === 'created') return {label: 'Оценка готова', className: s.statusCreated};
    if (status === 'creating') return {label: 'Оценивается', className: s.statusProcessing};
    if (status === 'updating') return {label: 'Переоценивается', className: s.statusProcessing};
    if (status === 'error') return {label: 'Ошибка оценки', className: s.statusError};
    return {label: 'В очереди', className: s.statusPending};
}

function getScoreMeta(score) {
    if (score === undefined || score === null) {
        return {
            value: '--',
            color: '#CBD5E1',
            progressDeg: 0,
        };
    }

    const normalized = Math.max(1, Math.min(10, Number(score)));
    let color = '#EF4444';
    if (normalized >= 8) color = '#22C55E';
    else if (normalized >= 6) color = '#F59E0B';

    return {
        value: normalized,
        color,
        progressDeg: normalized * 36,
    };
}

function toSortableScore(score) {
    if (score === undefined || score === null || score === '') return null;
    const parsed = Number(score);
    if (!Number.isFinite(parsed)) return null;
    return Math.max(1, Math.min(10, parsed));
}

function buildFullName(item) {
    const fullName = (item.full_name ?? '').trim();
    if (fullName.length > 0) return fullName;
    const parts = [item.last_name, item.first_name, item.middle_name]
        .map((value) => (value ?? '').trim())
        .filter((value) => value.length > 0);
    if (parts.length) return parts.join(' ');
    return '—';
}

export default function ResumeDashboardPage({BX24}) {
    const api = useMemo(() => getAPIClient(BX24), []);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [isLoading, setIsLoading] = useState(true);
    const [notificationText, setNotificationText] = useState('');
    const [notificationType, setNotificationType] = useState('error');

    const [vacancyId, setVacancyId] = useState(null);
    const [resumeItems, setResumeItems] = useState([]);
    const [targetIds, setTargetIds] = useState([]);

    const [resultById, setResultById] = useState({});
    const pollRef = useRef(null);

    const [selectedIds, setSelectedIds] = useState(new Set());
    const [expandedIds, setExpandedIds] = useState(new Set());

    const [stagesByDirection, setStagesByDirection] = useState({});
    const [sourceDirectionId, setSourceDirectionId] = useState(null);
    const [selectedStage, setSelectedStage] = useState(null);

    const [threshold, setThreshold] = useState({from: '', to: ''});

    const handleError = useCallback((defaultText, e) => {
        console.error(defaultText, e);
        setNotificationType('error');
        setNotificationText(e?.response?.data?.detail || defaultText);
    }, []);

    const openFullAnalysis = useCallback((crmItemId) => {
        const ids = targetIds.length ? targetIds.join(',') : resumeItems.map((item) => item.crm_item_id).join(',');
        const params = new URLSearchParams({
            crm_item_id: String(crmItemId),
            return_to: 'resume_dashboard',
            vacancy_id: String(vacancyId ?? ''),
            ids,
        });
        navigate(`/applicant_analys?${params.toString()}`);
    }, [navigate, targetIds, resumeItems, vacancyId]);

    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            try {
                BX24.fitWindow();
                const vId = safeNumber(searchParams.get('vacancy_id'));
                if (!vId) {
                    setNotificationType('error');
                    setNotificationText('Не передан vacancy_id для дашборда');
                    setIsLoading(false);
                    return;
                }
                setVacancyId(vId);

                const ids = parseIds(searchParams.get('ids'));
                setTargetIds(ids);
                setSourceDirectionId(safeNumber(searchParams.get('direction_id')));

                const directionsResponse = await api.get('/analysis_agent/applicant/directions');
                const directionOptions = (directionsResponse.data || []).map((d) => ({
                    ...d,
                    value: d.direction_id,
                    label: d.direction_name,
                }));

                const map = {};
                for (const direction of directionOptions) {
                    const params = {};
                    if (direction.direction_id !== 0) params.direction_id = direction.direction_id;
                    const stagesResponse = await api.get('/analysis_agent/applicant/stages', {params});
                    map[direction.direction_id] = (stagesResponse.data || []).map((stage) => ({
                        ...stage,
                        value: stage.stage_id,
                        label: stage.stage_name,
                    }));
                }
                setStagesByDirection(map);

            } catch (e) {
                handleError('Ошибка инициализации дашборда', e);
            } finally {
                setIsLoading(false);
            }
        };
        init();
    }, [BX24, searchParams, api, handleError]);

    const visibleTargetStages = useMemo(() => {
        const normalizedSourceId = sourceDirectionId !== null ? Number(sourceDirectionId) : null;
        if (normalizedSourceId !== null && !Number.isNaN(normalizedSourceId)) {
            return stagesByDirection[normalizedSourceId] || [];
        }

        const selectedDirections = resumeItems
            .filter((item) => selectedIds.has(item.crm_item_id))
            .map((item) => item.direction_id)
            .filter((value) => value !== null && value !== undefined);
        const uniqueDirections = Array.from(new Set(selectedDirections));
        if (uniqueDirections.length === 1) {
            return stagesByDirection[uniqueDirections[0]] || [];
        }
        return [];
    }, [sourceDirectionId, stagesByDirection, resumeItems, selectedIds]);

    const sortedResumeItems = useMemo(() => {
        return [...resumeItems].sort((a, b) => {
            const scoreA = toSortableScore(resultById[a.crm_item_id]?.score ?? a.ai_score);
            const scoreB = toSortableScore(resultById[b.crm_item_id]?.score ?? b.ai_score);

            if (scoreA === null && scoreB === null) return a.crm_item_id - b.crm_item_id;
            if (scoreA === null) return 1;
            if (scoreB === null) return -1;
            if (scoreB !== scoreA) return scoreB - scoreA;
            return a.crm_item_id - b.crm_item_id;
        });
    }, [resumeItems, resultById]);

    const loadResumes = useCallback(async () => {
        if (!vacancyId) return;
        setIsLoading(true);
        try {
            const response = await api.get('/analysis_agent/applicant/resumes', {
                params: {vacancy_b24_element_id: vacancyId},
            });
            const allResumes = response.data || [];
            const filtered = targetIds.length
                ? allResumes.filter((item) => targetIds.includes(item.crm_item_id))
                : allResumes;

            setResumeItems(filtered);
            setSelectedIds(new Set(filtered.map((item) => item.crm_item_id)));
            if (!targetIds.length) {
                setTargetIds(filtered.map((item) => item.crm_item_id));
            }
            BX24.fitWindow();
        } catch (e) {
            handleError('Ошибка загрузки резюме для дашборда', e);
        } finally {
            setIsLoading(false);
        }
    }, [api, vacancyId, targetIds, handleError, BX24]);

    useEffect(() => {
        if (!vacancyId) return;
        loadResumes();
    }, [vacancyId, loadResumes]);

    const fetchResults = useCallback(async () => {
        if (!targetIds.length) return;
        try {
            const response = await api.post('/analysis_agent/applicant/evaluate/batch/results', {
                crm_item_ids: targetIds,
            });
            const nextMap = {};
            (response.data?.items || []).forEach((item) => {
                nextMap[item.crm_item_id] = item;
            });
            setResultById(nextMap);

            const needsPolling = (response.data?.items || []).some((item) =>
                item.status === 'creating' || item.status === 'updating' || item.status === 'not_created'
            );

            if (pollRef.current) {
                clearTimeout(pollRef.current);
                pollRef.current = null;
            }
            if (needsPolling) {
                pollRef.current = setTimeout(fetchResults, 4000);
            }
        } catch (e) {
            handleError('Ошибка получения результатов пакетной оценки', e);
        }
    }, [api, targetIds, handleError]);

    useEffect(() => {
        if (targetIds.length === 0) return;
        fetchResults();
        return () => {
            if (pollRef.current) {
                clearTimeout(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [targetIds, fetchResults]);

    const toggleId = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectAll = () => {
        setSelectedIds((prev) => {
            if (prev.size === resumeItems.length) return new Set();
            return new Set(resumeItems.map((item) => item.crm_item_id));
        });
    };

    const selectByScore = () => {
        const from = threshold.from === "" ? 1 : Number(threshold.from);
        const to = threshold.to === "" ? 10 : Number(threshold.to);

        if (!Number.isFinite(from) || !Number.isFinite(to)) {
            setNotificationType("error");
            setNotificationText("Введите корректные значения порога");
            return;
        }
        if (from > to) {
            setNotificationType("error");
            setNotificationText("Порог от должен быть меньше или равен порогу до");
            return;
        }

        const picked = sortedResumeItems
            .filter((item) => {
                const score = toSortableScore(resultById[item.crm_item_id]?.score ?? item.ai_score);
                if (score === null) return false;
                return score >= from && score <= to;
            })
            .map((item) => item.crm_item_id);

        setSelectedIds(new Set(picked));
    };

    const moveSelected = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) {
            setNotificationType('error');
            setNotificationText('Выберите хотя бы одно резюме');
            return;
        }
        if (!selectedStage) {
            setNotificationType('error');
            setNotificationText('Выберите этап назначения');
            return;
        }

        setIsLoading(true);
        try {
            await api.post('/analysis_agent/applicant/move-to-stage', {
                crm_item_ids: ids,
                stage_id: selectedStage.stage_id,
            });
            setNotificationType('success');
            setNotificationText(`Перенесено резюме: ${ids.length}`);
            await loadResumes();
        } catch (e) {
            handleError('Ошибка массового переноса резюме', e);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleExpanded = (crmItemId) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(crmItemId)) next.delete(crmItemId);
            else next.add(crmItemId);
            return next;
        });
    };

    return (
        <div className={s.page}>
            <Loading isLoading={isLoading}/>
            <Header/>
            <div className={s.container}>
                <h1>Дашборд пакетной оценки</h1>
                <InlineNotification
                    type={notificationType}
                    text={notificationText}
                    hideFlag={!notificationText}
                />

                <div className={s.actionsButtons}>
                    <button
                        className={s.secondaryButton}
                        onClick={() => navigate(`/resume_selection?vacancy_id=${vacancyId}`)}
                    >
                        Вернуться к выбору резюме
                    </button>
                    <button className={s.secondaryButton} onClick={selectAll}>
                        {selectedIds.size === resumeItems.length ? 'Снять выбор' : 'Выбрать все'}
                    </button>
                </div>

                <div className={s.scoreTools}>
                    <div className={s.thresholdPanel}>
                        <div className={s.thresholdTitle}>Порог</div>
                        <div className={s.thresholdRange}>
                            <input
                                className={`${s.numberInput} ${s.thresholdInput}`}
                                placeholder={'от'}
                                type={'number'}
                                min={1}
                                max={10}
                                value={threshold.from}
                                onChange={(e) => setThreshold((prev) => ({...prev, from: e.target.value}))}
                            />
                            <span className={s.thresholdDash}>—</span>
                            <input
                                className={`${s.numberInput} ${s.thresholdInput}`}
                                placeholder={'до'}
                                type={'number'}
                                min={1}
                                max={10}
                                value={threshold.to}
                                onChange={(e) => setThreshold((prev) => ({...prev, to: e.target.value}))}
                            />
                        </div>
                        <button className={s.secondaryButton} onClick={selectByScore}>Выбрать</button>
                    </div>
                </div>

                <div className={`${s.moveBlock} ${s.compactSelectRow}`}>
                    <OneSelectUI
                        title={'Этап назначения'}
                        selectionOptions={visibleTargetStages}
                        selectedOption={selectedStage}
                        setSelectedOption={(value) => setSelectedStage(value || null)}
                        isClearable={true}
                        placeholder={'Выберите этап'}
                    />
                    <SubmitButton text={'Перенести выбранные'} onClick={moveSelected}/>
                </div>

                <div className={s.dashboardList}>
                    {sortedResumeItems.map((item) => {
                        const result = resultById[item.crm_item_id] || {};
                        const statusMeta = getStatusMeta(result.status || item.ai_status || 'not_created');
                        const scoreMeta = getScoreMeta(result.score ?? item.ai_score);
                        const displayName = buildFullName(item);
                        const desiredPosition = item.desired_position ?? '—';
                        const desiredSalary = item.desired_salary ?? '—';

                        return (
                            <div key={item.crm_item_id} className={s.dashboardRow}>
                                <div className={s.dashboardMain}>
                                    <input
                                        type={'checkbox'}
                                        checked={selectedIds.has(item.crm_item_id)}
                                        onChange={() => toggleId(item.crm_item_id)}
                                    />

                                    <div className={s.dashboardCell}>
                                        <div className={s.dashboardCaption}>ФИО</div>
                                        <div className={s.cardTitle}>{displayName}</div>
                                    </div>

                                    <div className={s.dashboardCell}>
                                        <div className={s.dashboardCaption}>Должность</div>
                                        <div className={s.cardMeta}>{desiredPosition}</div>
                                    </div>

                                    <div className={s.dashboardCell}>
                                        <div className={s.dashboardCaption}>Желаемая ЗП</div>
                                        <div className={s.cardMeta}>{desiredSalary}</div>
                                    </div>
                                </div>

                                <div className={s.dashboardActions}>
                                    <span className={`${s.statusBadge} ${statusMeta.className}`}>{statusMeta.label}</span>
                                    <div
                                        className={s.scoreCircleSmall}
                                        style={{
                                            background: `conic-gradient(${scoreMeta.color} ${scoreMeta.progressDeg}deg, #E2E8F0 0deg)`,
                                        }}
                                    >
                                        <div className={s.scoreCircleSmallInner}>{scoreMeta.value}</div>
                                    </div>
                                    <button
                                        type={'button'}
                                        className={s.secondaryButton}
                                        onClick={() => openFullAnalysis(item.crm_item_id)}
                                    >
                                        Полный анализ
                                    </button>
                                </div>

                                <button
                                    type={'button'}
                                    className={s.dashboardExpandToggle}
                                    onClick={() => toggleExpanded(item.crm_item_id)}
                                    aria-label={expandedIds.has(item.crm_item_id) ? 'Свернуть детали' : 'Развернуть детали'}
                                >
                                    <span className={s.expandToggleLabel}>
                                        {expandedIds.has(item.crm_item_id) ? 'Скрыть детали анализа' : 'Показать детали анализа'}
                                    </span>
                                    <span className={`${s.expandArrow} ${expandedIds.has(item.crm_item_id) ? s.expandArrowOpen : ''}`}/>
                                </button>

                                <div className={`${s.dashboardExpandedWrap} ${expandedIds.has(item.crm_item_id) ? s.dashboardExpandedWrapOpen : ''}`}>
                                    <div className={s.dashboardExpanded}>
                                        {result.green_flags?.length ? (
                                            <div className={s.goodText}>Плюсы: {result.green_flags.join('; ')}</div>
                                        ) : (
                                            <div className={s.neutralText}>Плюсы не найдены</div>
                                        )}
                                        {result.red_flags?.length ? (
                                            <div className={s.badText}>Минусы: {result.red_flags.join('; ')}</div>
                                        ) : (
                                            <div className={s.neutralText}>Минусы не найдены</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>

                {resumeItems.length === 0 && <div className={s.empty}>Нет данных для отображения</div>}
            </div>
        </div>
    );
}
